import {
  AttachmentMetadataType,
  DEFAULT_INSTRUCTION_SUBTITLE,
  DEFAULT_INSTRUCTION_TITLE,
  EmailAttachment,
  InteractionStepType,
} from '@scale/shared/types/chat';
import isNil from 'lodash/isNil';
import { memo, useContext, useEffect, useMemo } from 'react';
import {
  useChatStoreExtract,
  useMostRecentPrompt,
  useMostRecentResponse,
  useStepParams,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import { ChatMessageDisplayConfigButton } from '../../step/ChatMessageDisplayConfig';
import { PinButton } from '../../step/PinButton';
import { StepCard } from '../../step/StepCard';
import { StepWidth, StepWidthWrapper } from '../../step/StepWidthWrapper';
import { useIsRealCustomerAudit } from '../../../Chat/hooks/ChatStore.hooks';
import { useShowContributorInstructionsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useShowContributorInstructionsStore';
import { AttachmentViewer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AttachmentViewer';
import { Typography } from '@scale/scaleui';
import { emailSchema } from 'react-scale/components/scalers/tasks/textcollection/WorkspaceComponents/EmailView';

type InstructionProps = {
  complete?: boolean;
  readOnly?: boolean;
  responseIndex: number;
  isPinned: boolean;
};

export const Instruction = memo(
  ({ complete, responseIndex, readOnly, isPinned }: InstructionProps) => {
    const taskContext = useContext(ChatTaskContext);
    const config = useChatStoreExtract(state => state.config);
    const isRealCustomerAudit = useIsRealCustomerAudit();
    const customerAuditShowInstructions = useShowContributorInstructionsStore(
      state => state.customerAuditShowInstructions,
    );
    const step = useStepParams<InteractionStepType.Instruction>(responseIndex);
    const { onSubmit } = useStepSubmitHandler({
      responseIndex,
    });
    const {
      chatMessageDisplayConfig,
      useMarkdownDisplay,
      updateMarkdownConfig,
      toggleChatMessageDisplayType,
    } = useConfigureChatMessageDisplay({
      displayType: step.params.display_type,
    });

    const { instructions, enable_rtl_text } = step.params;

    const lastResponse = useMostRecentResponse(responseIndex);
    const lastPrompt = useMostRecentPrompt(responseIndex);

    const shouldAlwaysShowContributorInstructions = useUnleashFeatureFlag(
      'audit-always-show-contributor-instructions',
      { projectId: taskContext.projectId },
      false,
    );

    /* The output should always be the last response output unless this is a before step.
     * If this is a before step, we use the last prompt output.
     */
    const output =
      isNil(responseIndex) && responseIndex < config.before.length ? lastPrompt : lastResponse;

    useEffect(() => {
      if (!complete) {
        onSubmit(
          {
            type: InteractionStepType.Instruction,
            context: {},
            output,
          },
          responseIndex,
          step,
          undefined,
          true /* skip linting */,
        );
      }
    }, [complete, output, step, responseIndex, onSubmit]);

    const emailEntries = useMemo(() => {
      if (
        step.params.attachment_viewer_type === AttachmentMetadataType.Email &&
        step.params.attachment_viewer_config?.email_attachments_json
      ) {
        try {
          const emailEntries = JSON.parse(
            step.params.attachment_viewer_config?.email_attachments_json,
          ) as any[];

          const emailAttachments: EmailAttachment[] = [];
          // Validate each entry. If any entry is invalid, throw an error.
          for (const entry of emailEntries) {
            const parsedEntry = emailSchema.safeParse(entry);
            if (!parsedEntry.success) {
              return null;
            }
            emailAttachments.push({
              attachmentType: AttachmentMetadataType.Email,
              metadata: entry,
            });
          }

          return emailAttachments;
        } catch (e) {
          // no error catching behavior
        }
      }

      // Return null to indicate that at least one email attachment is invalid.
      return null;
    }, [
      step.params.attachment_viewer_config?.email_attachments_json,
      step.params.attachment_viewer_type,
    ]);

    if (
      isRealCustomerAudit &&
      !customerAuditShowInstructions &&
      !shouldAlwaysShowContributorInstructions
    ) {
      return null;
    }

    return (
      <StepWidthWrapper width={isPinned ? StepWidth.Full : StepWidth.Responsive}>
        <StepCard readOnly={readOnly}>
          <StepCard.Header>
            <StepCard.HeaderLeft>
              <StepCard.Icon icon="comment" />
              <StepCard.HeaderColumn>
                <StepCard.Title>{step.title || DEFAULT_INSTRUCTION_TITLE}</StepCard.Title>
                <StepCard.Subtitle>
                  {step.subtitle || DEFAULT_INSTRUCTION_SUBTITLE}
                </StepCard.Subtitle>
              </StepCard.HeaderColumn>
            </StepCard.HeaderLeft>
            <StepCard.HeaderRight>
              <PinButton responseIndex={responseIndex} />
              <ChatMessageDisplayConfigButton
                chatMessageDisplayConfig={chatMessageDisplayConfig}
                toggleChatMessageDisplayType={toggleChatMessageDisplayType}
                useMarkdownDisplay={useMarkdownDisplay}
                updateMarkdownConfig={updateMarkdownConfig}
              />
            </StepCard.HeaderRight>
          </StepCard.Header>
          {instructions && (
            <StepCard.Body>
              {useMarkdownDisplay ? (
                <ChatMarkdown
                  className="text-sm"
                  markdown={instructions}
                  enableHtml={taskContext.htmlMarkdownConfig?.enableInstructions}
                  enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                  enableSingleDollarTextMath={
                    chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                  }
                  enableExecutableCodeBlocks={
                    taskContext.codingConfig?.enableExecutableCodeMarkdown
                  }
                  enableRTL={enable_rtl_text}
                  enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
                />
              ) : (
                <div
                  dir={enable_rtl_text ? 'rtl' : undefined}
                  style={{ textAlign: 'start' }}
                  className="text-neutral-900 text-sm whitespace-pre-wrap"
                >
                  {instructions}
                </div>
              )}
            </StepCard.Body>
          )}
          {step.params.attachment_viewer_type === AttachmentMetadataType.Email &&
            step.params.attachment_viewer_config?.email_attachments_json && (
              <StepCard.Body>
                {emailEntries ? (
                  <AttachmentViewer attachments={emailEntries} />
                ) : (
                  <Typography.Paragraph3 className="text-danger-500">
                    Could not load email attachments. Please notify support.
                  </Typography.Paragraph3>
                )}
              </StepCard.Body>
            )}
        </StepCard>
      </StepWidthWrapper>
    );
  },
);
